import React, { useState } from 'react';
import styled from '@emotion/styled';
import { breakpoints, mq } from '@styles/breakpoints';
import { Icon } from '@components/common/generic-icon';
import { Theme, ThemeProps } from '@theme/base';
import { Collapse } from '@mui/material';
import { withTheme } from '@emotion/react';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { ReadMore } from '@styles/common';

export interface BaseTimeLineProps {
  items: Array<any>;
  renderTitle: (item: any, index: number) => React.ReactNode;
  renderBody: (item: any) => React.ReactNode;
  collapsedHeight?: number;
  theme?: Theme;
}

const Container = styled.div({
  position: 'relative',
  margin: '0 auto'
});

const TimelineCollapse = styled(Collapse)({
  paddingTop: 15,
  position: 'relative'
});

const ItemContainer: any = styled.div(({ isLast }: any) => ({
  paddingBottom: isLast ? 0 : 50,
  listStyle: 'none',
  position: 'relative',
  top: -10
}));

const ItemTail: any = styled.div(({ theme }: ThemeProps) => ({
  position: 'absolute',
  top: 10,
  left: 5,
  height: 'calc(100% - 10px)',
  borderLeft: `2px solid ${theme.custom.colors.group4.lighter}`
}));

const ItemHead: any = styled.div(({ theme }: ThemeProps) => ({
  position: 'absolute',
  width: 12,
  height: 12,
  border: `2px solid ${theme.custom.colors.group4.lighter}`,
  borderRadius: 100
}));

const ItemContent = styled.div({
  marginLeft: 20,
  top: -9,
  position: 'relative'
});

interface FadedProps extends ThemeProps {
  fade: boolean;
}
const Faded: any = styled.div(({ fade, theme }: FadedProps) => ({
  position: 'absolute',
  width: '100%',
  background: `linear-gradient(0, ${theme.custom.colors.white}, rgba(255,255,255,0))`,
  height: fade ? 300 : 0,
  bottom: 0,
  [mq.ie(breakpoints.small)]: {
    background: 'url(/assets/images/description-gradient.png) bottom left repeat-x'
  }
}));

const ItemTitle: any = styled.div(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group4.light,
  ...(theme.custom.typography.h5 as any),
  textTransform: 'uppercase',
  marginBottom: 10
}));

const Body = styled.div({
  marginLeft: 10
});

export const BaseTimeLineComponent = ({ collapsedHeight, items, renderTitle, renderBody }: BaseTimeLineProps) => {
  const canCollapse: boolean = !!collapsedHeight;
  const [expanded, setExpanded] = useState(!canCollapse);
  const handleOnClick = () => setExpanded(!expanded);
  const t: any = useI18NextContext();
  return (
    <Container data-testid={TestId.timeline.container}>
      <TimelineCollapse in={expanded} collapsedSize={collapsedHeight}>
        {items.map((item: any, index: number) => (
          <ItemContainer key={index} isLast={index + 1 === items.length}>
            {index + 1 < items.length && <ItemTail />}
            <ItemHead />
            <ItemContent>
              <ItemTitle>{renderTitle(item, index)}</ItemTitle>
              <Body>{renderBody(item)}</Body>
            </ItemContent>
          </ItemContainer>
        ))}
        <Faded fade={!expanded} />
      </TimelineCollapse>
      {canCollapse && (
        <ReadMore onClick={handleOnClick} data-testid={TestId.timeline.readMore}>
          <span>{expanded ? t('common__description--view-less') : t('itinerary__collapse--view-all')}</span>
          <Icon name={expanded ? 'mhi-chevron-up' : 'mhi-chevron-down'} />
        </ReadMore>
      )}
    </Container>
  );
};

export default withTheme(BaseTimeLineComponent);
